import { DEFAULT_VISIBLE_PAGES, PAGE_TYPES, PageType } from '@app-types/PageType'
import { useNativeSilaReminders } from '../hooks/useNativeSilaReminders'
import useDebounce from '@hooks/useDebounce'
import { createUserDataChanges } from 'helpers/userDataChanges'
import {
  addPendingUserDataChanges,
  writeLocalUserData,
  withUserDataTimestamp,
} from 'helpers/userDataSyncState'
import { useUserDataSync } from 'hooks/useUserDataSync'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'react-hot-toast'
import { backupStats, detectStatsReset } from '../helpers/indexedDbBackup'
import { UserDataType } from '../types/UserDataType'
import { useAuth } from './AuthContext'

export type VisibilityType = 'show' | 'hide'

/** Return the current ISO timestamp for initial user-data defaults. */
const getNowIso = () => new Date().toISOString()

// Create initial page values
const initialPageValues = Object.fromEntries(
  PAGE_TYPES.map((pageType) => [pageType, pageType === 'kuran' ? 0 : 1]),
) as Record<PageType, number>

export const initialState: UserDataType = {
  ...initialPageValues,
  kuranType: 'default',
  arabicFontAlignment: 'right',
  kuranMealSource: 'muhtasar_tefsir',
  kuranMealType: 'show',
  kuranTooltip: 'show',
  autoScroll: 'show',
  savedPages: [],
  hatims: [],
  duaArabicVisibility: 'show',
  duaMealVisibility: 'show',
  duaLatinVisibility: 'show',
  delayedStatsEntry: false,
  kazaNamaz: {
    sabah: 0,
    ogle: 0,
    ikindi: 0,
    aksam: 0,
    yatsi: 0,
    oruc: 0,
  },
  reminders: [],
  arabicFontFamily: 'arab-ver-3',
  zikirs: {},
  stats: {},
  notes: '',
  intros: [],
  homePageButtons: Object.fromEntries(
    PAGE_TYPES.map((pageType) => [pageType, DEFAULT_VISIBLE_PAGES.includes(pageType)]),
  ) as Record<PageType, boolean>,
  showReadingTime: true,
  showConnectionsWidget: true,
  showPrayerTimes: true,
  showDailyGoal: true,
  customStats: [],
  lastReadPages: {},
  uid: null,
  email: null,
  displayName: null,
  createdAt: getNowIso(),
  updatedAt: null,
  fieldUpdatedAt: {},
  readingTime: {
    today: 0,
    thisWeek: 0,
    total: 0,
    lastUpdated: new Date().toISOString().split('T')[0],
  },
  readingChallenge: undefined,
}

type UseUserDataContextType = {
  updateUserData: (_key: keyof UserDataType, _value: any) => void
  updateMultipleUserData: (_data: Partial<UserDataType>) => void
  getAndSetUserData: () => Promise<void>
  userData: UserDataType
  loading: boolean
}

const UserData = createContext<any>(null)

/**
 * Warn during development when an update passes back the same object it holds:
 * that means a component edited `userData` in place, and the change is invisible
 * to sync. Components must pass new arrays and objects instead.
 */
const warnAboutInPlaceEdits = (
  previousUserData: UserDataType,
  updates: Partial<UserDataType>,
) => {
  if (process.env.NODE_ENV === 'production') return

  Object.entries(updates).forEach(([field, value]) => {
    const isObject = typeof value === 'object' && value !== null
    if (isObject && value === previousUserData[field as keyof UserDataType]) {
      console.warn(`userData.${field} was edited in place; pass a new value so it syncs.`)
    }
  })
}

/**
 * Record the fields an update really changes as pending cloud changes. The
 * comparison uses this tab's previous record, never the stored copy that all tabs
 * share, so another tab's newer values are not recorded as this tab's edits.
 */
const recordPendingChanges = (
  previousUserData: UserDataType,
  updates: Partial<UserDataType>,
) => {
  warnAboutInPlaceEdits(previousUserData, updates)
  addPendingUserDataChanges(createUserDataChanges(previousUserData, updates, Date.now()))
}

/** Access the current user-data state and its local-update actions. */
export const useUserData = () => useContext<UseUserDataContextType>(UserData)

export const UserDataProvider = ({ children }: { children: ReactNode }) => {
  const [userData, setUserData] = useState<UserDataType>(initialState)
  const debouncedUserData = useDebounce(userData, 1000)
  const { user, loading: userLoading } = useAuth()
  const handleDataLoaded = useCallback(async (data: UserDataType) => {
    const isReset = await detectStatsReset(data.stats)
    if (!isReset) return

    console.warn('Stats reset detected, notifying user about recovery option')
    setTimeout(() => {
      toast.error(
        'İstatistikleriniz silinmiş görünüyor. Hesabım sayfasından istatistiklerinizi kurtarabilirsiniz.',
        { duration: 7000, icon: '⚠️', id: 'stats-reset-notification' },
      )
    }, 1500)
  }, [])
  const { loading, getAndSetUserData } = useUserDataSync({
    user,
    userLoading,
    userData,
    initialState,
    setUserData,
    onDataLoaded: handleDataLoaded,
  })

  useNativeSilaReminders(userData.reminders, loading)

  useEffect(() => {
    if (!loading && userData.stats && Object.keys(userData.stats).length > 0) {
      backupStats(userData.stats).catch((error) =>
        console.error('Failed to backup stats:', error),
      )
    }
  }, [debouncedUserData.stats, loading])

  /** Update one user-data field and record it as a pending cloud change immediately. */
  const updateUserData = async (key: string, value: any) => {
    if (loading) return

    setUserData((previousUserData) => {
      const updates = { [key]: value } as Partial<UserDataType>
      const data = withUserDataTimestamp(
        { ...previousUserData, ...updates },
        initialState,
      )
      recordPendingChanges(previousUserData, updates)
      writeLocalUserData(data)
      return data
    })
  }

  /** Update several user-data fields atomically and record them as pending cloud changes. */
  const updateMultipleUserData = async (data: Partial<UserDataType>) => {
    if (loading) return

    if (data.stats && userData.stats) {
      const isReset =
        Object.keys(data.stats).length === 0 && Object.keys(userData.stats).length > 0

      if (isReset) {
        console.warn('Detected potential stats reset in updateMultipleUserData')
        return
      }
    }

    setUserData((previousUserData) => {
      const newUserData = withUserDataTimestamp(
        { ...previousUserData, ...data },
        initialState,
      )
      recordPendingChanges(previousUserData, data)
      writeLocalUserData(newUserData)
      return newUserData
    })
  }

  return (
    <UserData.Provider
      value={{
        userData,
        loading,
        getAndSetUserData,
        updateUserData,
        updateMultipleUserData,
      }}
    >
      {children}
    </UserData.Provider>
  )
}
